import React, { useState, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { signs, Sign } from '../../data/signs';
import { ParticleField } from '../shared/ParticleField';
// Psychological microcopy per Reino — shown on each card to convey
// emotional/archetypal essence (not astrology).
const SIGN_MICROCOPY: Record<string, string> = {
  aries: 'El fuego que no sabe detenerse.',
  tauro: 'La necesidad de sentir estabilidad.',
  geminis: 'La mente que nunca se apaga.',
  cancer: 'La emoción que absorbe todo.',
  leo: 'La necesidad de ser visto realmente.',
  virgo: 'La mente que intenta controlarlo todo.',
  libra: 'El miedo silencioso al conflicto.',
  escorpio: 'La intensidad que escondes.',
  sagitario: 'La necesidad constante de escapar.',
  capricornio: 'El peso invisible que cargas.',
  acuario: 'La sensación de nunca pertenecer.',
  piscis: 'El océano emocional que llevas dentro.'
};
export function SignGrid() {
  const navigate = useNavigate();
  const [selectedSign, setSelectedSign] = useState<Sign | null>(null);
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const handleSignClick = (sign: Sign) => {
    // Scroll to top immediately while the portal overlay covers the screen,
    // so the user never sees the destination page scroll up after transition.
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
    setSelectedSign(sign);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
      navigate(`/${sign.id}`);
    }, 1400);
  };
  return <section id="sign-grid" className="relative py-20 px-6 bg-cosmic-black" data-id="element-398">
      <div className="container mx-auto max-w-7xl" data-id="element-399">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-4 max-w-3xl mx-auto leading-tight" data-id="element-400">
          Selecciona el Reino que más resuena contigo
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center text-gold-light/70 mb-16 italic max-w-xl mx-auto text-[18px]" data-id="element-401">
          No elijas desde la lógica. Elige el que despierta algo dentro de ti.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-402">
          {signs.map((sign, index) => {
          const isActive = activeCard === sign.id;
          return <motion.div key={sign.id} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.08
          }} onClick={() => handleSignClick(sign)} onMouseEnter={() => setActiveCard(sign.id)} onMouseLeave={() => setActiveCard(null)} onTouchStart={() => setActiveCard(sign.id)} className="relative group cursor-pointer" data-id="element-403">
                <div className="relative overflow-hidden rounded-xl border-2 border-opacity-30 transition-all duration-500 group-hover:border-opacity-100" style={{
              borderColor: sign.colors.primary,
              boxShadow: isActive ? `0 0 50px ${sign.colors.primary}80, inset 0 0 80px ${sign.colors.primary}40` : `0 0 20px ${sign.colors.primary}20`
            }} data-id="element-404">
                  {/* Image */}
                  <div className="relative h-[28rem] overflow-hidden bg-cosmic-darker" data-id="element-405">
                    <img src={sign.imageUrl} alt={sign.entidad} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" style={{
                  filter: isActive ? 'brightness(1.15) contrast(1.08) saturate(1.15)' : 'brightness(0.85)'
                }} loading="lazy" data-id="element-406" />

                    {/* Sign-color wash overlay — appears on hover (mobile-safe, no mix-blend-mode) */}
                    <motion.div className="absolute inset-0 pointer-events-none" initial={{
                  opacity: 0
                }} animate={{
                  opacity: isActive ? 0.25 : 0
                }} transition={{
                  duration: 0.4
                }} style={{
                  backgroundColor: sign.colors.primary
                }} data-id="element-407" />

                    {/* Inner glow on hover */}
                    <motion.div className="absolute inset-0 pointer-events-none" initial={{
                  opacity: 0
                }} animate={{
                  opacity: isActive ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} style={{
                  boxShadow: `inset 0 0 100px ${sign.colors.primary}80`
                }} data-id="element-408" />

                    {/* Bottom gradient overlay */}
                    <div className="absolute inset-0 opacity-70 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none" style={{
                  background: `linear-gradient(to top, #050508 0%, ${sign.colors.primary}20 40%, transparent 70%)`
                }} data-id="element-409" />

                    {/* Sign-specific particles on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-410">
                      <ParticleField type={sign.particleType} color={sign.colors.primary} density={20} data-id="element-411" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none" data-id="element-412">
                    <div className="flex items-center justify-between mb-2" data-id="element-413">
                      <h3 className="text-2xl font-serif font-bold text-white" style={{
                    textShadow: '0 2px 12px rgba(0,0,0,0.9)'
                  }} data-id="element-414">
                        {sign.name}
                      </h3>
                      <span className="text-4xl opacity-70 group-hover:opacity-100 transition-opacity" style={{
                    color: sign.colors.primary,
                    textShadow: `0 0 20px ${sign.colors.primary}`
                  }} data-id="element-415">
                        {sign.symbol}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200 mb-2" style={{
                  textShadow: '0 1px 8px rgba(0,0,0,0.9)'
                }} data-id="element-416">
                      {sign.arquetipo}
                    </p>

                    {/* Psychological microcopy — always visible */}
                    <p className="text-xs md:text-sm italic font-serif leading-snug" style={{
                  color: sign.colors.accent,
                  textShadow: '0 1px 8px rgba(0,0,0,0.95)'
                }} data-id="element-417">
                      {SIGN_MICROCOPY[sign.id] || sign.phrase}
                    </p>

                    <motion.p initial={{
                  opacity: 0,
                  height: 0
                }} animate={isActive ? {
                  opacity: 1,
                  height: 'auto'
                } : {
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="text-xs italic mt-2 overflow-hidden" style={{
                  color: sign.colors.accent,
                  textShadow: '0 1px 8px rgba(0,0,0,0.9)'
                }} data-id="element-418">
                      {sign.phrase}
                    </motion.p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Portal transition overlay */}
      <AnimatePresence data-id="element-419">
        {selectedSign && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-id="element-420">
            {/* Color flood */}
            <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 3,
          opacity: 1
        }} transition={{
          duration: 1.2,
          ease: 'easeInOut'
        }} className="absolute inset-0" style={{
          background: `radial-gradient(circle, ${selectedSign.colors.primary} 0%, ${selectedSign.colors.secondary} 50%, #050508 100%)`
        }} data-id="element-421" />

            {/* Sign-specific particles */}
            <ParticleField type={selectedSign.particleType} color={selectedSign.colors.primary} density={80} data-id="element-422" />

            {/* Sign image zoom transition */}
            <motion.div initial={{
          scale: 0.3,
          opacity: 0
        }} animate={{
          scale: [0.3, 1, 1.8],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 1.4,
          ease: 'easeOut',
          times: [0, 0.5, 1]
        }} className="absolute inset-0 flex items-center justify-center" data-id="element-423">
              <div className="relative w-[80vw] max-w-2xl aspect-[3/4] rounded-2xl overflow-hidden" style={{
            boxShadow: `0 0 100px ${selectedSign.colors.primary}, 0 0 200px ${selectedSign.colors.primary}80`
          }} data-id="element-424">
                <img src={selectedSign.imageUrl} alt={selectedSign.entidad} className="w-full h-full object-cover" data-id="element-425" />
                <div className="absolute inset-0" style={{
              background: `radial-gradient(circle at center, transparent 30%, ${selectedSign.colors.primary}60 100%)`
            }} data-id="element-426" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}